<template>
  <div class="onr-admin-layout">
    <button
        type="button"
        class="onr-admin-layout__mobile-toggle"
        :aria-expanded="mobileNavOpen ? 'true' : 'false'"
        aria-label="Toggle admin menu"
        @click="mobileNavOpen = !mobileNavOpen"
    >
      <span></span><span></span><span></span>
    </button>

    <div
        v-if="mobileNavOpen"
        class="onr-admin-layout__backdrop"
        @click="mobileNavOpen = false"
    ></div>

    <aside
        class="onr-admin-layout__sidebar"
        :class="{ 'onr-admin-layout__sidebar--open': mobileNavOpen }"
    >
      <div class="onr-admin-layout__header">
        <span class="onr-admin-layout__logo">TDI Admin</span>
        <span v-if="adminEmail" class="onr-admin-layout__identity">
          {{ adminEmail }}<template v-if="adminRole"> &middot; {{ adminRole }}</template>
        </span>
      </div>

      <nav class="onr-admin-layout__nav" @click="mobileNavOpen = false">
        <router-link to="/admin" class="onr-admin-layout__link" exact-active-class="onr-admin-layout__link--active">
          Dashboard
        </router-link>

        <div class="onr-admin-layout__group">
          <span class="onr-admin-layout__group-label">People</span>
          <router-link to="/admin/teachers" class="onr-admin-layout__link" active-class="onr-admin-layout__link--active">
            {{ currentSeasonTeachersLabel }}
          </router-link>
          <router-link to="/admin/all-teachers" class="onr-admin-layout__link" active-class="onr-admin-layout__link--active">
            All Teachers
          </router-link>
          <router-link to="/admin/students" class="onr-admin-layout__link" active-class="onr-admin-layout__link--active">
            {{ currentSeasonStudentsLabel }}
          </router-link>
          <router-link to="/admin/all-students" class="onr-admin-layout__link" active-class="onr-admin-layout__link--active">
            All Students
          </router-link>
        </div>

        <div class="onr-admin-layout__group">
          <span class="onr-admin-layout__group-label">Program</span>
          <router-link to="/admin/classes" class="onr-admin-layout__link" active-class="onr-admin-layout__link--active">
            Classes
          </router-link>
          <router-link to="/admin/reports" class="onr-admin-layout__link" active-class="onr-admin-layout__link--active">
            Reports
          </router-link>
          <router-link to="/admin/matching" class="onr-admin-layout__link" active-class="onr-admin-layout__link--active">
            Matching
          </router-link>
        </div>

        <div class="onr-admin-layout__group">
          <span class="onr-admin-layout__group-label">Operations</span>
          <router-link to="/admin/video-links" class="onr-admin-layout__link" active-class="onr-admin-layout__link--active">
            Video Links
          </router-link>
          <router-link to="/admin/reminders" class="onr-admin-layout__link" active-class="onr-admin-layout__link--active">
            Reminders
          </router-link>
          <router-link v-if="adminRole === 'coordinator' || adminRole === 'admin'" to="/admin/dreamers-blessing" class="onr-admin-layout__link" active-class="onr-admin-layout__link--active">
            Dreamers Blessing
          </router-link>
        </div>

        <div v-if="adminRole === 'admin'" class="onr-admin-layout__group">
          <span class="onr-admin-layout__group-label">Settings</span>
          <router-link to="/admin/settings/admin-accounts" class="onr-admin-layout__link" active-class="onr-admin-layout__link--active">
            Admin Access
          </router-link>
        </div>
      </nav>
    </aside>

    <main class="onr-admin-layout__content">
      <!-- Keyed by path so /teachers/A → /teachers/B remounts the detail page
           instead of reusing it with A's edit form and cached data. -->
      <router-view :key="$route.path"></router-view>
    </main>
  </div>
</template>

<script>
import { getIsAdmin, getAdminSeasons } from '@/api/admin';
import { seasonLabel } from '@/domain/seasons';
import { auth } from '@/firebase.config';

export default {
  name: 'AdminLayout',
  data() {
    return {
      adminRole: null,
      adminEmail: null,
      mobileNavOpen: false,
      currentSeasonId: null,
    };
  },
  computed: {
    // Falls back to the plain label when there's no defined current season
    // (e.g. an undefined gap between seasons) — never shows something like
    // "null Teachers".
    currentSeasonTeachersLabel() {
      return this.currentSeasonId ? `${seasonLabel(this.currentSeasonId)} Teachers` : 'Teachers';
    },
    currentSeasonStudentsLabel() {
      return this.currentSeasonId ? `${seasonLabel(this.currentSeasonId)} Students` : 'Students';
    },
  },
  watch: {
    // Belt-and-suspenders close (the nav's own @click already closes it on a
    // link tap) — also covers programmatic navigation and back/forward.
    '$route'() {
      this.mobileNavOpen = false;
    },
  },
  async mounted() {
    // Best-effort only: this is purely for display in the sidebar header.
    // Access control already happened in the router guard before this
    // component ever mounted, so a failure here must never block rendering
    // or trigger a redirect.
    this.adminEmail = auth.currentUser ? auth.currentUser.email : null;
    try {
      const info = await getIsAdmin();
      this.adminRole = info && info.role ? info.role : null;
      if (info && info.email) {
        this.adminEmail = info.email;
      }
    } catch (error) {
      console.error(error);
    }

    // Also best-effort, purely for the "Season 12 Teachers"/"Season 12
    // Students" nav labels — a failure here just falls back to the plain
    // "Teachers"/"Students" label via the computed properties above.
    try {
      const { currentSeason } = await getAdminSeasons();
      this.currentSeasonId = currentSeason || null;
    } catch (error) {
      console.error(error);
    }
  },
};
</script>

<style scoped lang="scss">
@use '../../utils/_colors.scss' as *;

$onr-admin-mobile-breakpoint: 768px;

.onr-admin-layout {
  display: flex;
  min-height: 100vh;

  &__mobile-toggle {
    background-color: $blue-1;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    display: none;
    flex-direction: column;
    gap: 4px;
    height: 40px;
    justify-content: center;
    padding: 0 9px;
    position: fixed;
    right: .75rem;
    top: .75rem;
    width: 40px;
    z-index: 30;

    span {
      background-color: $white-1;
      border-radius: 2px;
      display: block;
      height: 2px;
      width: 100%;
    }

    @media (max-width: $onr-admin-mobile-breakpoint) {
      display: flex;
    }
  }

  &__backdrop {
    background-color: rgba(0, 0, 0, .45);
    display: none;
    inset: 0;
    position: fixed;
    z-index: 15;

    @media (max-width: $onr-admin-mobile-breakpoint) {
      display: block;
    }
  }

  &__sidebar {
    background-color: $blue-1;
    color: $white-1;
    display: flex;
    flex: 0 0 220px;
    flex-direction: column;
    padding: 1.25rem 0;

    @media (max-width: $onr-admin-mobile-breakpoint) {
      bottom: 0;
      left: 0;
      overflow-y: auto;
      position: fixed;
      top: 0;
      transform: translateX(-100%);
      transition: transform .2s ease;
      width: min(80vw, 260px);
      z-index: 20;

      &--open {
        transform: translateX(0);
      }
    }
  }

  &__header {
    border-bottom: 1px solid rgba(255, 255, 255, .15);
    display: flex;
    flex-direction: column;
    gap: .2rem;
    margin-bottom: .75rem;
    padding: 0 1.25rem 1rem;
  }

  &__logo {
    font-size: 1.05rem;
    font-weight: 800;
    letter-spacing: .02em;
  }

  &__identity {
    color: rgba(255, 255, 255, .65);
    font-size: .75rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__nav {
    display: flex;
    flex-direction: column;
    gap: .1rem;
  }

  &__group {
    display: flex;
    flex-direction: column;
    margin-top: .9rem;
  }

  &__group-label {
    color: rgba(255, 255, 255, .5);
    font-size: .68rem;
    font-weight: 700;
    letter-spacing: .08em;
    padding: 0 1.25rem .35rem;
    text-transform: uppercase;
  }

  &__link {
    color: rgba(255, 255, 255, .85);
    font-size: .9rem;
    font-weight: 600;
    padding: .5rem 1.25rem;
    text-decoration: none;

    &:hover {
      background-color: rgba(255, 255, 255, .08);
    }

    &--active {
      background-color: rgba(255, 255, 255, .14);
      color: $white-1;
    }
  }

  &__content {
    background-color: $neutral-3;
    flex: 1 1 auto;
    min-width: 0;
    padding: 1.5rem 2rem;

    @media (max-width: $onr-admin-mobile-breakpoint) {
      // Extra top padding clears the fixed hamburger toggle so page headers
      // never sit underneath it.
      padding: 3.5rem 1rem 1.5rem;
    }
  }
}
</style>
